import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, Leaf, DollarSign, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Marketplace = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/offers`);
      setOffers(response.data);
    } catch (error) {
      console.error('Failed to fetch offers', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-zinc-400">Loading marketplace...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6 md:px-12 lg:px-24">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            Créditos de Carbono <span className="text-primary">Marketplace</span>
          </h1>
          <p className="text-zinc-400 text-lg mb-12">
            Navegue por ofertas verificadas de créditos de carbono de proprietários de terras e projetos ambientais
          </p>

          {offers.length === 0 ? (
            <div className="text-center py-24">
              <Leaf className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-bold mb-2">Nenhuma oferta ainda</h3>
              <p className="text-zinc-400">Seja o primeiro a listar créditos de carbono!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="marketplace-grid">
              {offers.map((offer, index) => (
                <motion.div
                  key={offer.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <Link to={`/offers/${offer.id}`}>
                    <Card
                      className="bg-card border border-border rounded-sm overflow-hidden hover:border-primary/50 transition-all duration-300 h-full group"
                      data-testid={`offer-card-${offer.id}`}
                    >
                      <div
                        className="h-40 bg-gradient-to-br from-zinc-900 to-zinc-800 grayscale group-hover:grayscale-0 transition-all duration-300"
                        style={{
                          backgroundImage: 'url(https://images.unsplash.com/photo-1731508257431-05eed6a6fc45?crop=entropy&cs=srgb&fm=jpg&q=85)',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />
                      <CardContent className="p-6 space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Badge className="rounded-full px-3 py-0.5 text-xs font-mono uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                              {offer.biome}
                            </Badge>
                            <span className="text-xs text-zinc-500">
                              {new Date(offer.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <h3 className="font-heading font-bold text-lg mb-2 line-clamp-2">
                            {offer.description.substring(0, 60)}{offer.description.length > 60 ? '...' : ''}
                          </h3>
                          <p className="text-sm text-zinc-400 mb-1">por {offer.seller_name}</p>
                        </div>

                        <div className="space-y-2 pt-2 border-t border-border">
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2 text-zinc-400">
                              <Leaf className="w-4 h-4" strokeWidth={1.5} />
                              Credits
                            </span>
                            <span className="font-mono font-bold text-primary">
                              {offer.carbon_credits.toLocaleString()} tCO₂
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2 text-zinc-400">
                              <MapPin className="w-4 h-4" strokeWidth={1.5} />
                              Forest Area
                            </span>
                            <span className="font-mono">
                              {offer.forest_area.toLocaleString()} ha
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2 text-zinc-400">
                              <DollarSign className="w-4 h-4" strokeWidth={1.5} />
                              Price
                            </span>
                            <span className="font-mono font-bold text-lg">
                              ${offer.price.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Marketplace;
